import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner,
  Dropdown, DropdownButton, Pagination
} from 'react-bootstrap';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const CalendarEventsManager = () => {
  const [events, setEvents] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('all');
  
  // API configuration
  const API_BASE_URL = BASE_URL;
  // const UPLOAD_BASE_URL = UPLOAD_BASE_URL;
  
  const [formData, setFormData] = useState({
    academic_year_id: '',
    event_type: 'college',
    title: '',
    description: '',
    document_file: ''
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    fetchAcademicYears();
    fetchEvents();
  }, []);

  const fetchAcademicYears = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/calendar_of_events.php?academic_years=true`);
      if (response.data.success) {
        setAcademicYears(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching academic years:', err);
    }
  };

  const fetchEvents = async (academicYearId = null) => {
    try {
      setLoading(true);
      setError('');
      
      let url = `${API_BASE_URL}/calendar_of_events.php`;
      if (academicYearId && academicYearId !== 'all') {
        url += `?academic_year_id=${academicYearId}`;
      }
      
      const response = await axios.get(url);
      
      if (response.data.success) {
        setEvents(response.data.data);
      } else {
        setError('Failed to load events: ' + response.data.message);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Error connecting to server: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      setUploadingDocument(true);
      setUploadProgress(0);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('document', file);
      
      const response = await axios.post(
        `${API_BASE_URL}/upload_calendar_event_document.php`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          document_file: response.data.file_path
        }));
        setSuccess('Document uploaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to upload document: ' + response.data.message);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Error uploading document: ' + err.message);
    } finally {
      setUploadingDocument(false);
      setUploadProgress(0);
      e.target.value = ''; // Reset file input
    }
  };

  const handleRemoveDocument = () => {
    if (formData.document_file) {
      // Optionally send request to delete file from server
      setFormData(prev => ({
        ...prev,
        document_file: ''
      }));
    }
  };

  const handleAcademicYearFilter = (academicYearId) => {
    setSelectedAcademicYear(academicYearId);
    fetchEvents(academicYearId === 'all' ? null : academicYearId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const dataToSend = { ...formData };
      
      if (editingEvent) {
        const response = await axios.put(
          `${API_BASE_URL}/calendar_of_events.php?id=${editingEvent.id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data.success) {
          setSuccess('Event updated successfully!');
        } else {
          setError('Operation failed: ' + (response.data.message || ''));
          return;
        }
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/calendar_of_events.php`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data.success) {
          setSuccess('Event added successfully!');
        } else {
          setError('Operation failed: ' + (response.data.message || ''));
          return;
        }
      }
      
      setShowModal(false);
      resetForm();
      setEditingEvent(null);
      fetchEvents(selectedAcademicYear === 'all' ? null : selectedAcademicYear);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Submit error:', err);
      setError('Error saving event: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(
          `${API_BASE_URL}/calendar_of_events.php?id=${id}`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        
        if (response.data.success) {
          setSuccess('Event deleted successfully!');
          fetchEvents(selectedAcademicYear === 'all' ? null : selectedAcademicYear);
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete event: ' + (response.data.message || ''));
        }
      } catch (err) {
        console.error('Delete error:', err);
        setError('Error deleting event: ' + err.message);
      }
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      academic_year_id: event.academic_year_id,
      event_type: event.event_type,
      title: event.title,
      description: event.description || '',
      document_file: event.document_file || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      academic_year_id: '',
      event_type: 'college',
      title: '',
      description: '',
      document_file: ''
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingEvent(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDocumentUrl = (filePath) => {
    if (!filePath) return null;
    return `${UPLOAD_BASE_URL}${filePath}`;
  };

  const getFileIcon = (filePath) => {
    if (!filePath) return 'fa-file';
    
    const extension = filePath.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'fa-file-word';
      case 'xls':
      case 'xlsx':
        return 'fa-file-excel';
      case 'ppt':
      case 'pptx':
        return 'fa-file-powerpoint';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'fa-file-image';
      case 'txt':
        return 'fa-file-alt';
      default:
        return 'fa-file';
    }
  };

  const getFileTypeBadge = (filePath) => {
    if (!filePath) return null;
    
    const extension = filePath.split('.').pop().toLowerCase();
    let bg = 'secondary';
    let text = extension.toUpperCase();
    
    switch (extension) {
      case 'pdf':
        bg = 'danger';
        break;
      case 'doc':
      case 'docx':
        bg = 'primary';
        break;
      case 'xls':
      case 'xlsx':
        bg = 'success';
        break;
      case 'ppt':
      case 'pptx':
        bg = 'warning';
        break;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        bg = 'info';
        break;
    }
    
    return <Badge bg={bg} className="me-1">{text}</Badge>;
  };

  if (loading) {
    return (
      <>
        <AdminNavbar user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading events...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Calendar of Events Manager</h2>
            <p className="text-muted">Manage college and university events</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => setShowModal(true)}>
              <i className="fas fa-plus me-2"></i>Add Event
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        {/* Filter Section */}
        <Card className="mb-4">
          <Card.Body>
            <Row className="align-items-center">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Filter by Academic Year</Form.Label>
                  <Form.Select
                    value={selectedAcademicYear}
                    onChange={(e) => handleAcademicYearFilter(e.target.value)}
                  >
                    <option value="all">All Academic Years</option>
                    {academicYears.map((year) => (
                      <option key={year.id} value={year.id}>
                        {year.academic_year}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={9} className="text-end">
                <Badge bg="info" className="me-3">
                  Total Events: {events.length}
                </Badge>
                <Badge bg="primary" className="me-2">
                  <i className="fas fa-university me-1"></i>
                  University: {events.filter(e => e.event_type === 'university').length}
                </Badge>
                <Badge bg="secondary">
                  <i className="fas fa-graduation-cap me-1"></i>
                  College: {events.filter(e => e.event_type === 'college').length}
                </Badge>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th width="40">#</th>
                  <th>Event Title</th>
                  <th width="100">Type</th>
                  <th>Academic Year</th>
                  <th>Description</th>
                  <th width="120">Document</th>
                  <th width="120">Created</th>
                  <th width="120">Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.length > 0 ? (
                  events.map((event, index) => (
                    <tr key={event.id}>
                      <td>{index + 1}</td>
                      <td>
                        <strong>{event.title}</strong>
                      </td>
                      <td>
                        <Badge bg={event.event_type === 'university' ? 'primary' : 'secondary'}>
                          <i className={`fas fa-${event.event_type === 'university' ? 'university' : 'graduation-cap'} me-1`}></i>
                          {event.event_type}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg="info">
                          {event.academic_year || 'N/A'}
                        </Badge>
                      </td>
                      <td>
                        <small className="text-muted">
                          {event.description 
                            ? (event.description.length > 100 
                                ? `${event.description.substring(0, 100)}...` 
                                : event.description)
                            : 'No description'}
                        </small>
                      </td>
                      <td>
                        {event.document_file ? (
                          <div className="d-flex align-items-center">
                            {getFileTypeBadge(event.document_file)}
                            <a 
                              href={getDocumentUrl(event.document_file)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-decoration-none"
                              title="View document"
                            >
                              <i className={`fas ${getFileIcon(event.document_file)} text-primary`}></i>
                            </a>
                          </div>
                        ) : (
                          <span className="text-muted small">No file</span>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">
                          {formatDate(event.created_at)}
                        </small>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          onClick={() => handleEdit(event)}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(event.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-5">
                      <i className="fas fa-calendar-alt fa-3x text-muted mb-3"></i>
                      <p className="text-muted">No events found</p>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => setShowModal(true)}
                      >
                        <i className="fas fa-plus me-1"></i> Add First Event
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* Add/Edit Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingEvent ? 'Edit Event' : 'Add New Event'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Academic Year *</Form.Label>
                    <Form.Select
                      name="academic_year_id"
                      value={formData.academic_year_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Academic Year</option>
                      {academicYears.map((year) => (
                        <option key={year.id} value={year.id}>
                          {year.academic_year }
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Event Type *</Form.Label>
                    <Form.Select
                      name="event_type"
                      value={formData.event_type}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="college">College Event</option>
                      <option value="university">University Event</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Event Title *</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Annual Cultural Festival, Convocation Ceremony"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Provide detailed description of the event..."
                />
              </Form.Group>
              
              {/* Document Upload Section */}
              <Card className="mb-3">
                <Card.Header>
                  <h6 className="mb-0">
                    <i className="fas fa-file-upload me-2"></i>
                    Event Document (Optional)
                  </h6>
                  <small className="text-muted">Upload PDF, Word, Excel, PowerPoint, Images, or TXT (Max 10MB)</small>
                </Card.Header>
                <Card.Body>
                  {/* Upload Progress */}
                  {uploadingDocument && (
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <small>Uploading...</small>
                        <small>{uploadProgress}%</small>
                      </div>
                      <div className="progress" style={{ height: '5px' }}>
                        <div 
                          className="progress-bar" 
                          role="progressbar" 
                          style={{ width: `${uploadProgress}%` }}
                          aria-valuenow={uploadProgress}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        ></div>
                      </div>
                    </div>
                  )}
                  
                  {/* Current Document */}
                  {formData.document_file ? (
                    <div className="mb-3">
                      <Alert variant="info" className="py-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <i className={`fas ${getFileIcon(formData.document_file)} me-2`}></i>
                            <span className="me-2">{formData.document_file.split('/').pop()}</span>
                            {getFileTypeBadge(formData.document_file)}
                          </div>
                          <div>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="me-2"
                              href={getDocumentUrl(formData.document_file)}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="View document"
                            >
                              <i className="fas fa-eye"></i>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={handleRemoveDocument}
                              title="Remove document"
                            >
                              <i className="fas fa-times"></i>
                            </Button>
                          </div>
                        </div>
                      </Alert>
                    </div>
                  ) : (
                    <Alert variant="light" className="text-center py-4">
                      <i className="fas fa-file-upload fa-2x text-muted mb-3"></i>
                      <p className="text-muted mb-2">No document uploaded</p>
                    </Alert>
                  )}
                  
                  {/* Upload Button */}
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {formData.document_file ? 'Replace Document' : 'Upload Document'}
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
                      onChange={handleDocumentUpload}
                      disabled={uploadingDocument}
                    />
                    <Form.Text className="text-muted">
                      Max file size: 10MB. Allowed: PDF, Word, Excel, PowerPoint, Images, TXT
                    </Form.Text>
                  </Form.Group>
                </Card.Body>
              </Card>
              
              {/* Preview Section */}
              <Card className="border-secondary">
                <Card.Header>
                  <h6 className="mb-0">
                    <i className="fas fa-eye me-2"></i>
                    Preview
                  </h6>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h5 className="mb-0">{formData.title || 'Event Title'}</h5>
                    <div>
                      <Badge bg={formData.event_type === 'university' ? 'primary' : 'secondary'}>
                        {formData.event_type || 'college'}
                      </Badge>
                    </div>
                  </div>
                  
                  {formData.academic_year_id && (
                    <div className="mb-2">
                      <strong>Academic Year:</strong>{' '}
                      {academicYears.find(y => y.id == formData.academic_year_id)?.academic_year || 'N/A'}
                    </div>
                  )}
                  
                  {formData.description && (
                    <div className="mb-3">
                      <strong>Description:</strong>
                      <p className="text-muted mb-0 mt-1">{formData.description}</p>
                    </div>
                  )}
                  
                  {formData.document_file && (
                    <div className="d-flex align-items-center">
                      <i className={`fas ${getFileIcon(formData.document_file)} me-2 text-primary`}></i>
                      <span className="text-truncate" style={{ maxWidth: '200px' }}>
                        {formData.document_file.split('/').pop()}
                      </span>
                      {getFileTypeBadge(formData.document_file)}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploadingDocument}>
                {uploadingDocument ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Uploading...
                  </>
                ) : (
                  editingEvent ? 'Update Event' : 'Add Event'
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default CalendarEventsManager;